import {Campaign} from './campaign';
import {AnalysisResult} from './campaignVideoAnalysis';
import {ProfileProps} from './creatorProfile';

export const CONTENT_SUBMISSION_ANALYSIS_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const;

export type ContentSubmissionAnalysisStatus =
  (typeof CONTENT_SUBMISSION_ANALYSIS_STATUS)[keyof typeof CONTENT_SUBMISSION_ANALYSIS_STATUS];

export const CONTENT_SUBMISSION_STATUS = {
  PENDING: 'pending',
  APPROVED: 'accepted',
  ARCHIVED: 'archived',
  REJECTED: 'rejected',
} as const;

export type ContentSubmissionState =
  (typeof CONTENT_SUBMISSION_STATUS)[keyof typeof CONTENT_SUBMISSION_STATUS];

export const CONTENT_SUBMISSION_KIND = {
  PHOTO: 'photo',
  VIDEO: 'video',
  BLOG: 'blog',
  MIXED_MEDIA: 'mixed_media',
} as const;

export type ContentSubmissionKind =
  (typeof CONTENT_SUBMISSION_KIND)[keyof typeof CONTENT_SUBMISSION_KIND];

export interface ContentSubmissionComment {
  id: string;
  content: string;
  email?: string | null;
  fullName?: string | null;
  createdAt: string;
  dashboardUser?: {id: string; displayName?: string | null} | null;
  contentSubmissionVersionId?: string | null;
  step?: string | null;
}

export interface ContentSubmissionApprovalStatus {
  campaignApprovalStepId: string;
  step: string;
  approved: boolean;
  approvedBy: {id: string; name: string} | null;
  createdAt: string | null;
}

export interface ContentSubmissionApproval {
  id: string;
  contentSubmissionVersionId: string;
  step: string;
  approved: boolean;
  approvedBy: {id: string; name: string} | null;
  createdAt: string;
}

export interface ContentSubmission {
  id: string;
  kind: string;
  deliverableKind?: string | null;
  acceptedAt?: string | null;
  aasmState: string;
  aasmStateDate: string;
  /** Prior state when item is archived (pending or rejected); null otherwise. */
  archivedFromState?: string | null;
  /** Client-only field set when optimistically moving between phases. */
  previousAasmState?: string;
  approvalStatus?: ContentSubmissionApprovalStatus[];
  note?: string | null;
  noteHtml?: string | null;
  contentUrl?: string | null;
  downloadUrl?: string | null;
  thumbnailUrl?: string | null;
  thumbnailUrls: string[];
  url?: string | null;
  brandMessageHtml?: string | null;
  brandMessage?: string | null;
  merchantMessageHtml?: string | null;
  merchantMessage?: string | null;
  weight: number;
  parentContentSubmissionId: string | null;
  childContentSubmissionCount: number;
  childContentSubmissions?: ContentSubmission[];
  acceptedChildContentSubmissionCount: number;
  contractId?: string | null;
  latestVersionCommentCount: number;
  latestVersionId?: string | null;
  versionCount: number;
  latestVersionAnalysisStatus?: ContentSubmissionAnalysisStatus | null;
  latestVersionCampaignFitScore?: number | null;
  hasCampaignVideoAnalysis?: boolean;
  campaignVideoAnalysis?: ContentSubmissionAnalysis | null;
  campaign?: Campaign | null;
  contract?: unknown | null;
  paidGig?: unknown | null;
  profile?: ProfileProps | null;
  stateTransitions?: unknown[];
  versions?: ContentSubmissionVersion[];
  dueAt: string | null;
  dueSoon: boolean;
}

export interface ContentSubmissionAnalysis {
  id: string;
  status: ContentSubmissionAnalysisStatus;
  analysisResult: AnalysisResult | null;
  campaignFitScore: number;
  alignmentSummary: string;
  analysisIssues: Array<{
    description: string;
    timestampSeconds: number;
  }>;
  keyStrengths: string[];
  areasForImprovement: string[];
  formattedIssues: Array<{
    description: string;
    timestamp: string;
  }>;
  contentMandatoriesChecklist: Array<{
    requirement: string;
    fulfilled: boolean;
    explanation: string;
  }>;
  creatorFeedbackMessage: string;
  errorMessage: string | null;
  /** User rating 1–5 stars; editable via modal */
  analysisRating?: number | null;
  /** User note; editable via modal */
  analysisNote?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ContentSubmissionVersion {
  id: string;
  url: string | null;
  thumbnailUrl: string;
  downloadUrl: string | null;
  note: string;
  noteHtml: string;
  brandMessageHtml: string | null;
  submittedAt?: string | null;
  createdAt?: string;
  feedbackProvidedAt?: string | null;
  terminalStateForRevision?: string | null;
  kind: string;
  versionCount?: number;
  weight?: number;
  versions?: unknown[];
  contentSubmissionAnalysisStatus?: ContentSubmissionAnalysisStatus | null;
  contentSubmissionAnalysisCampaignFitScore?: number | null;
  approvals?: ContentSubmissionApproval[];
  contentSubmissionComments?: ContentSubmissionComment[];
  /** Campaign and profile are on nested contentSubmission; some APIs also expose profile at root */
  contentSubmission?: ContentSubmission | null;
  profile?: ProfileProps | null;
  contentSubmissionAnalysis?: ContentSubmissionAnalysis | null;
}
